import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useGetPastStay } from 'src/features/stay/api/useGetPastStay';
import { useParking } from 'src/providers/ParkingProvider';
import { useListPastStayPeriods } from 'src/features/stay/api/useListPastStayPeriods';
import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { useListActiveStayPeriods } from 'src/features/stay/api/useListActiveStayPeriods';
import { useGetActiveStay } from 'src/features/stay/api/useGetActiveStay';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import ActiveStaySidePanelButtons from 'src/features/stay/components/sidePanel/ActiveStaySidePanelButtons';
import { STAY_TYPE } from 'src/features/stay/types';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import SidePanelTabs from 'src/features/stay/components/sidePanel/SidePanelTabs';
import OverviewActiveStays from 'src/features/stay/components/sidePanel/OverviewActiveStays';
import ActiveStayPeriods from 'src/features/stay/components/sidePanel/ActiveStayPeriods';
import useSorting from 'src/registry/hooks/useSorting';

type StaySidePanelProps = {
    isActiveStay: boolean;
    selectedStayId?: string;
    // The entries widget should show the entry (earliest) gateway pass first; the exits widget keeps
    // the default order (exit/latest first).
    showEntryFirst?: boolean;
};

const StaySidePanel = ({ isActiveStay, selectedStayId, showEntryFirst = false }: StaySidePanelProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { querySortingOptions } = useSorting({
        initialSortingState: [{ id: 'start', desc: true }],
    });

    const { data: selectedPastStay } = useGetPastStay({
        stayId: selectedStayId ?? '',
        parkingId,
        config: {
            enabled: !!selectedStayId && !isActiveStay,
        },
    });

    const { data: pastStayPeriodsData } = useListPastStayPeriods({
        stayId: selectedStayId ?? '',
        parkingId,
        config: { enabled: !!selectedStayId && !isActiveStay },
        ...querySortingOptions,
    });

    const { data: filteredGatewayPassesByStayId } = useListGatewayPasses({
        ...(isActiveStay ? { active_stay: selectedStayId ?? '' } : { past_stay: selectedStayId ?? '' }),
        parkingId,
        limit: 500,
        ordering: 'time',
        config: { enabled: !!selectedStayId },
    });

    const { data: selectedActiveStay } = useGetActiveStay({
        stayId: selectedStayId ?? '',
        parkingId,
        config: {
            enabled: !!selectedStayId && isActiveStay,
        },
    });

    const { data: activeStayPeriodsData } = useListActiveStayPeriods({
        stayId: selectedStayId ?? '',
        parkingId,
        config: { enabled: !!selectedStayId && isActiveStay },
    });

    const gatewayPassesPhotos = React.useMemo(() => {
        const passes = filteredGatewayPassesByStayId?.results ?? [];
        // Sorting ascending by time puts the entry (earliest) pass at index 0, which the carousel shows
        // first. The default keeps the API order (exit/latest first) so the exits widget is unaffected.
        const orderedPasses = showEntryFirst
            ? [...passes].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
            : passes;
        return orderedPasses.map((pass) => {
            return {
                image: pass.photo,
                title: pass.gateway.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(pass.time)}`,
            };
        });
    }, [filteredGatewayPassesByStayId?.results, formatMessage, locale, showEntryFirst]);

    const stayPeriodsData = isActiveStay ? activeStayPeriodsData?.results : pastStayPeriodsData?.results;
    const stayDataToUse = isActiveStay ? selectedActiveStay : selectedPastStay;

    const badgeTheme = isActiveStay ? 'green' : 'red';
    const badgeTitleTranslation = isActiveStay ? visitorMessages.activeStay : visitorMessages.pastStay;
    return (
        <>
            {stayDataToUse ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(visitorMessages.parkingStay)}
                        </DialogTitle>
                        <Badge theme={badgeTheme}>
                            <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                            {formatMessage(badgeTitleTranslation)}
                        </Badge>
                    </div>
                    <DialogDescription>{`#${stayDataToUse.ref}`}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <ImageCarousel imageSlides={gatewayPassesPhotos} />
                <ActiveStaySidePanelButtons
                    stayId={selectedStayId}
                    stayType={isActiveStay ? STAY_TYPE.ACTIVE : STAY_TYPE.PAST}
                    stay={selectedActiveStay}
                />
                <SidePanelTabs
                    tabs={[
                        {
                            key: 'overview',
                            label: formatMessage(visitorMessages.overview),
                            content: (
                                <OverviewActiveStays
                                    selectedStay={stayDataToUse}
                                    type={isActiveStay ? STAY_TYPE.ACTIVE : STAY_TYPE.PAST}
                                />
                            ),
                        },
                        {
                            key: 'periods',
                            label: formatMessage(visitorMessages.periods),
                            content: <ActiveStayPeriods stayPeriodsData={stayPeriodsData} />,
                        },
                    ]}
                    defaultActiveKey="overview"
                />
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default StaySidePanel;
